import { useCallback, useEffect, useRef, useState } from 'react'

type Theme = 'light' | 'dark'
const STORAGE_KEY = 'cc-theme'

function getInitial(): Theme {
  if (typeof window === 'undefined') return 'light'
  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored === 'light' || stored === 'dark') return stored
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

let fadeTimer: ReturnType<typeof setTimeout> | undefined

function applyTheme(theme: Theme, fade = false) {
  const root = document.documentElement
  // 7 Oct 2026 (Apple polish): ease dark <-> light instead of a hard flash,
  // unless the user asked for reduced motion.
  const reduce =
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (fade && !reduce) {
    root.classList.add('theme-fading')
    if (fadeTimer) clearTimeout(fadeTimer)
    fadeTimer = setTimeout(() => root.classList.remove('theme-fading'), 260)
  }
  root.classList.toggle('dark', theme === 'dark')
}

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(getInitial)

  const first = useRef(true)
  useEffect(() => {
    applyTheme(theme, !first.current) // no fade on the first paint
    first.current = false
    window.localStorage.setItem(STORAGE_KEY, theme)
  }, [theme])

  const toggle = useCallback(() => {
    setTheme((t) => (t === 'light' ? 'dark' : 'light'))
  }, [])

  return { theme, toggle } as const
}
